import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import {
  CLASSES_ESCOLHIVEIS,
  ROTULOS_DE_CLASSE_DO_TIPO,
  ROTULOS_DE_MOTIVO_DE_CONFERENCIA,
  ROTULOS_DE_ORIGEM_DA_CLASSIFICACAO,
  textoDaModalidade,
  type CampoDaTriagem,
  type ClasseDoTipo,
  type ClassificacaoDaTriagem,
  type OperadoraClassificada,
} from '../../shared/triagem/tipos';
import { corrigirClassificacao } from './api/corrigirClassificacao';
import { chavesDeTriagem } from './api/filaDeTriagem';

/** O texto lido que a pessoa vê: o valor da MÁQUINA, nunca o `valorHumano` que a própria correção grava. */
function textoLido(campo: CampoDaTriagem): string | null {
  if (!campo.tentado || !campo.encontrado) return null;
  const texto = campo.valor?.trim() ?? '';
  return texto === '' ? null : texto;
}

/**
 * O nome da operadora na lista. `nome` não é único no cadastro (só o código é): quando dois itens têm o mesmo nome,
 * o código vai entre parênteses para a pessoa não escolher às cegas.
 */
function rotuloDaOperadora(operadora: OperadoraClassificada, todas: readonly OperadoraClassificada[]): string {
  const repetido = todas.some((outra) => outra.id !== operadora.id && outra.nome === operadora.nome);
  return repetido ? `${operadora.nome} (${operadora.codigo})` : operadora.nome;
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

/**
 * O que a pessoa precisa saber antes de escolher: o que o e-mail trazia e por que o sistema pede conferência.
 * Frase de operação — sem "vocabulário", sem "apelido".
 */
function motivoEmFrase(classificacao: ClassificacaoDaTriagem, tipoLido: string | null, operadoraLida: string | null): string[] {
  return classificacao.motivos.map((motivo) => {
    if (motivo === 'RequestTypeOutOfVocabulary') {
      return tipoLido === null
        ? 'O tipo do pedido não é nenhum dos tipos da lista.'
        : `O tipo do pedido (“${tipoLido}”) não é nenhum dos tipos da lista.`;
    }
    if (motivo === 'PayerWithoutAlias') {
      return operadoraLida === null
        ? 'A operadora lida no e-mail não está no cadastro.'
        : `A operadora lida no e-mail (“${operadoraLida}”) não está no cadastro.`;
    }
    if (motivo === 'PayerDivergesFromSender') {
      return operadoraLida === null
        ? 'A operadora citada no e-mail não é a de quem enviou.'
        : `A operadora citada no e-mail (“${operadoraLida}”) não é a de quem enviou.`;
    }
    return `${ROTULOS_DE_MOTIVO_DE_CONFERENCIA[motivo]}: o sistema e o agente leram coisas diferentes.`;
  });
}

interface Props {
  readonly ticketId: string;
  /** `null` = ainda não lido. */
  readonly classificacao: ClassificacaoDaTriagem | null;
  readonly operadorasParaEscolha: readonly OperadoraClassificada[];
  /** O que o parser leu, para a pessoa ver ao lado da escolha. */
  readonly tipoLido: CampoDaTriagem;
  readonly operadoraLida: CampoDaTriagem;
  /** Recarrega o detalhe — o que o `409` pede. */
  readonly aoAtualizar: () => void;
}

/**
 * Tipo e operadora do ticket (`TP-038`, `T7`): o que o sistema entendeu, de onde veio, e a escolha de quem opera.
 *
 * Só aparece opção da lista (`D5`): nunca campo livre. Já corrigido por pessoa, só mostra o resultado: a API não
 * aceita uma segunda escolha (`409`).
 */
export function ConferenciaDeTipoEOperadora({
  ticketId,
  classificacao,
  operadorasParaEscolha,
  tipoLido,
  operadoraLida,
  aoAtualizar,
}: Props): React.JSX.Element {
  const idDoTitulo = useId();
  const navegar = useNavigate();
  const clienteDeConsultas = useQueryClient();

  const textoDoTipo = textoLido(tipoLido);
  const textoDaOperadora = textoLido(operadoraLida);

  const classeLida = classificacao?.classeDoTipo ?? null;
  const classeInicial: ClasseDoTipo | '' = classeLida !== null && classeLida !== 'OutOfVocabulary' ? classeLida : '';
  const [classe, definirClasse] = useState<ClasseDoTipo | ''>(classeInicial);
  const [operadoraId, definirOperadoraId] = useState<string>(classificacao?.operadora?.id ?? '');
  // O apelido fica numa tabela sem retenção e vale para todos os e-mails: só existe quando a pessoa pede.
  const [lembrarTexto, definirLembrarTexto] = useState(false);
  const [editando, definirEditando] = useState(classificacao?.aConferir === true);
  const [enviando, definirEnviando] = useState(false);
  const [jaConfirmouAgora, definirJaConfirmouAgora] = useState(false);
  const [problema, definirProblema] = useState<'conflito' | 'falha' | null>(null);

  // Os botões que mudam de estado desmontam a si mesmos ou o formulário inteiro: sem levar o foco adiante ele cai
  // no corpo da página e o Tab recomeça do topo. `focoPendente` diz para onde ir quando a nova tela estiver montada.
  const formularioRef = useRef<HTMLFormElement>(null);
  const botaoCorrigirRef = useRef<HTMLButtonElement>(null);
  const confirmacaoRef = useRef<HTMLDivElement>(null);
  const focoPendente = useRef<'formulario' | 'corrigir' | 'confirmacao' | null>(null);

  const eHumana = classificacao?.origem === 'Human';
  const completa = classe !== '' && operadoraId !== '';
  // Depois do `409` e de "Atualizar" a classificação volta já corrigida por outra pessoa: o formulário some.
  const mostrarFormulario = editando && !eHumana;

  useEffect(() => {
    const alvo = focoPendente.current;
    focoPendente.current = null;
    if (alvo === 'formulario') {
      formularioRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
    } else if (alvo === 'corrigir') {
      botaoCorrigirRef.current?.focus();
    } else if (alvo === 'confirmacao') {
      confirmacaoRef.current?.focus();
    }
  }, [editando, jaConfirmouAgora]);

  const confirmar = (): void => {
    if (!completa) return;
    definirEnviando(true);
    definirProblema(null);
    corrigirClassificacao(ticketId, {
      classeDoTipo: classe,
      operadoraId,
      lembrarTexto: lembrarTexto && textoDaOperadora !== null,
    })
      .then(() => {
        focoPendente.current = 'confirmacao';
        definirJaConfirmouAgora(true);
        definirEditando(false);
        // A fila e o detalhe saem do cache: a fila mostra a escolha sem recarregar a página.
        void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeTriagem.raiz });
      })
      .catch((causa: unknown) => {
        if (causa instanceof ErroDaApi && causa.status === 401) {
          void navegar('/entrar', { replace: true });
          return;
        }
        definirProblema(causa instanceof ErroDaApi && causa.status === 409 ? 'conflito' : 'falha');
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  if (classificacao === null) {
    return (
      <Box component="section" aria-labelledby={idDoTitulo}>
        <Typography id={idDoTitulo} variant="subtitle2" component="h3">
          Tipo e operadora
        </Typography>
        <Chip label="Ainda não lido" size="small" variant="outlined" sx={{ mt: 0.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          O sistema ainda não leu o tipo e a operadora deste e-mail. A conferência aparece depois da leitura.
        </Typography>
      </Box>
    );
  }

  const operadoraEscolhida = operadorasParaEscolha.find((o) => o.id === operadoraId) ?? null;

  return (
    <Box component="section" aria-labelledby={idDoTitulo}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography id={idDoTitulo} variant="subtitle2" component="h3">
          Tipo e operadora
        </Typography>
        {classificacao.aConferir ? <Chip label="Conferir tipo e operadora" size="small" color="warning" variant="outlined" /> : null}
        <Typography variant="caption" color="text.secondary">
          {ROTULOS_DE_ORIGEM_DA_CLASSIFICACAO[classificacao.origem]}
        </Typography>
      </Stack>

      {/* Sempre montado: leitor de tela só anuncia o que muda dentro de uma região que já existia. */}
      <Box ref={confirmacaoRef} role="status" tabIndex={-1}>
        {jaConfirmouAgora ? (
          <Alert severity="success" role="none" sx={{ mt: 1 }}>
            Tipo e operadora confirmados. A fila já mostra a escolha.
          </Alert>
        ) : null}
      </Box>

      {!mostrarFormulario ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Linha rotulo="Tipo entendido">
            {classificacao.classeDoTipo === null ? 'Tipo não informado' : ROTULOS_DE_CLASSE_DO_TIPO[classificacao.classeDoTipo]}
          </Linha>
          {/* TP-039: a modalidade que o tipo indica é a que o Caso recebe na promoção, como sugestão do e-mail. */}
          <Linha rotulo="Modalidade">{textoDaModalidade(classificacao) ?? 'Sem modalidade'}</Linha>
          <Linha rotulo="Operadora entendida">
            {classificacao.operadora === null ? 'Operadora não identificada' : classificacao.operadora.nome}
          </Linha>
          {textoDoTipo !== null || textoDaOperadora !== null ? (
            <Typography variant="caption" color="text.secondary" component="div">
              O e-mail trazia: {[textoDoTipo, textoDaOperadora].filter((t) => t !== null).map((t) => `“${t}”`).join(' · ')}
            </Typography>
          ) : null}
          {!eHumana ? (
            <Button
              ref={botaoCorrigirRef}
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'start' }}
              onClick={() => {
                focoPendente.current = 'formulario';
                definirEditando(true);
                definirJaConfirmouAgora(false);
              }}
            >
              Corrigir tipo e operadora
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Stack
          component="form"
          ref={formularioRef}
          spacing={1.5}
          sx={{ mt: 1 }}
          onSubmit={(evento: React.FormEvent) => {
            evento.preventDefault();
            confirmar();
          }}
        >
          {classificacao.aConferir ? (
            <Stack spacing={0.25}>
              {motivoEmFrase(classificacao, textoDoTipo, textoDaOperadora).map((frase) => (
                <Typography key={frase} variant="body2">
                  {frase}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Escolha o tipo e a operadora corretos. A escolha vale uma vez só e não muda sozinha numa nova leitura.
            </Typography>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              select
              size="small"
              label="Tipo correto"
              value={classe}
              disabled={enviando}
              required
              onChange={(evento) => {
                definirClasse(evento.target.value as ClasseDoTipo | '');
              }}
              sx={{ minWidth: 220, flex: 1 }}
            >
              {CLASSES_ESCOLHIVEIS.map((opcao) => (
                <MenuItem key={opcao} value={opcao}>
                  {ROTULOS_DE_CLASSE_DO_TIPO[opcao]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Operadora correta"
              value={operadoraId}
              disabled={enviando || operadorasParaEscolha.length === 0}
              required
              onChange={(evento) => {
                definirOperadoraId(evento.target.value);
              }}
              helperText={operadorasParaEscolha.length === 0 ? 'Nenhuma operadora cadastrada para escolher.' : undefined}
              sx={{ minWidth: 220, flex: 1 }}
            >
              {operadorasParaEscolha.map((opcao) => (
                <MenuItem key={opcao.id} value={opcao.id}>
                  {rotuloDaOperadora(opcao, operadorasParaEscolha)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {textoDaOperadora !== null ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={lembrarTexto}
                  disabled={enviando}
                  onChange={(evento) => {
                    definirLembrarTexto(evento.target.checked);
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Reconhecer “{textoDaOperadora}”{operadoraEscolhida === null ? '' : ` como ${rotuloDaOperadora(operadoraEscolhida, operadorasParaEscolha)}`} nos próximos
                  e-mails
                </Typography>
              }
            />
          ) : null}

          {problema === 'conflito' ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={aoAtualizar}>
                  Atualizar
                </Button>
              }
            >
              Outra pessoa já conferiu o tipo e a operadora deste pedido. Atualize para ver o que ficou.
            </Alert>
          ) : null}
          {problema === 'falha' ? (
            <Alert severity="error">Não foi possível salvar a escolha. Confira os dois campos e tente de novo.</Alert>
          ) : null}

          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" size="small" disabled={!completa || enviando}>
              Confirmar tipo e operadora
            </Button>
            {classificacao.aConferir ? null : (
              <Button
                size="small"
                disabled={enviando}
                onClick={() => {
                  focoPendente.current = 'corrigir';
                  definirEditando(false);
                  definirProblema(null);
                }}
              >
                Cancelar
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
